import { useState, useEffect, useRef } from 'react';

interface UseTypingEffectOptions {
    texts: string[];
    typingSpeed?: number;
    deletingSpeed?: number;
    pauseBeforeDelete?: number;
    pauseBeforeType?: number;
    initialDelay?: number;
}

type Phase = 'initial' | 'typing' | 'pauseAfterType' | 'deleting' | 'pauseAfterDelete';

/**
 * Easing function for ease-in (starts slow, speeds up)
 * Used for typing effect
 */
const easeIn = (progress: number): number => {
    return progress * progress;
};

/**
 * Easing function for ease-out (starts fast, slows down)
 * Used for deleting effect
 */
const easeOut = (progress: number): number => {
    return 1 - Math.pow(1 - progress, 2);
};

/**
 * Custom hook for typewriter effect that cycles through multiple texts.
 * Starts with a blinking cursor, then types the first text, deletes it,
 * types the next text, and so on.
 * 
 * Features ease-in for typing (starts slow, speeds up) and
 * ease-out for deleting (starts fast, slows down).
 */
export const useTypingEffect = ({
    texts,
    typingSpeed = 80,
    deletingSpeed = 50,
    pauseBeforeDelete = 2000,
    pauseBeforeType = 2000,
    initialDelay = 5000,
}: UseTypingEffectOptions) => {
    const [displayText, setDisplayText] = useState('');
    const [phase, setPhase] = useState<Phase>('initial');
    const textIndexRef = useRef(0);
    const charIndexRef = useRef(0);

    useEffect(() => {
        if (texts.length === 0) return;

        let timeout: NodeJS.Timeout;

        const getCurrentText = () => texts[textIndexRef.current];

        /**
         * Calculate delay for typing with ease-in effect
         * Starts slow, speeds up as we type more characters
         */
        const getTypingDelay = (currentChar: number, totalChars: number): number => {
            const progress = currentChar / totalChars;
            const easedProgress = easeIn(progress);
            // Invert: high delay at start (slow), low delay at end (fast)
            // Range from typingSpeed * 2 down to typingSpeed * 0.5
            const minSpeed = typingSpeed * 0.5;
            const maxSpeed = typingSpeed * 2;
            return maxSpeed - (easedProgress * (maxSpeed - minSpeed));
        };

        /**
         * Calculate delay for deleting with ease-out effect
         * Starts fast, slows down as we delete more characters
         */
        const getDeletingDelay = (currentChar: number, totalChars: number): number => {
            const progress = 1 - (currentChar / totalChars);
            const easedProgress = easeOut(progress);
            // Start fast, end slow
            // Range from deletingSpeed * 0.5 up to deletingSpeed * 2
            const minSpeed = deletingSpeed * 0.5;
            const maxSpeed = deletingSpeed * 2;
            return minSpeed + (easedProgress * (maxSpeed - minSpeed));
        };

        switch (phase) {
            case 'initial':
                // Initial delay with blinking cursor
                timeout = setTimeout(() => {
                    setPhase('typing');
                }, initialDelay);
                break;

            case 'typing':
                const currentTextForTyping = getCurrentText();
                if (charIndexRef.current < currentTextForTyping.length) {
                    const delay = getTypingDelay(charIndexRef.current, currentTextForTyping.length);
                    timeout = setTimeout(() => {
                        charIndexRef.current += 1;
                        setDisplayText(currentTextForTyping.slice(0, charIndexRef.current));
                    }, delay);
                } else {
                    // Finished typing, pause before deleting
                    setPhase('pauseAfterType');
                }
                break;

            case 'pauseAfterType':
                timeout = setTimeout(() => {
                    setPhase('deleting');
                }, pauseBeforeDelete);
                break;

            case 'deleting':
                const currentTextForDeleting = getCurrentText();
                if (charIndexRef.current > 0) {
                    const delay = getDeletingDelay(charIndexRef.current, currentTextForDeleting.length);
                    timeout = setTimeout(() => {
                        charIndexRef.current -= 1;
                        setDisplayText(currentTextForDeleting.slice(0, charIndexRef.current));
                    }, delay);
                } else {
                    // Finished deleting, move to next text and pause
                    textIndexRef.current = (textIndexRef.current + 1) % texts.length;
                    setPhase('pauseAfterDelete');
                }
                break;

            case 'pauseAfterDelete':
                timeout = setTimeout(() => {
                    setPhase('typing');
                }, pauseBeforeType);
                break;
        }

        return () => {
            if (timeout) clearTimeout(timeout);
        };
    }, [phase, displayText, texts, typingSpeed, deletingSpeed, pauseBeforeDelete, pauseBeforeType, initialDelay]);

    return {
        displayText,
        isDeleting: phase === 'deleting',
        hasStarted: phase !== 'initial'
    };
};
